import { useEffect, useRef } from 'react';

/**
 * Zatvaranje modalnog dijaloga tasterom Escape.
 *
 * Kopija `src/hooks/useEscapeToClose.ts` - `apps/admin` je zasebna Vite aplikacija
 * sa svojim tsconfig-om i ne uvozi iz root `src/`. Stek je po aplikaciji, sto je
 * ispravno: dijalozi iz POS-a i admin panela nikad nisu na istom ekranu.
 *
 * Uveden posto je klik na backdrop uklonjen (dijalozi drze nesacuvan unos pa je
 * promasen klik gubio podatke - vidi `docs/design/design-system.md` §10). Escape
 * je tada ostao jedina brza precica, pa mora da radi svuda i predvidivo.
 *
 * **Zasto stek, a ne obican `window` listener po dijalogu:** kad su dva dijaloga
 * otvorena (npr. `ConfirmDialog` iznad forme), svi `keydown` listeneri na
 * `window`-u okidaju se istovremeno i jedan Escape bi zatvorio OBA. Stek pamti
 * redosled montiranja pa reaguje samo najgornji - sto korisnik i ocekuje.
 */

// ─── Stek (izdvojen kao cista logika da moze da se testira u node okruzenju;
//     vitest ovde nema jsdom, pa `window` u testu ne postoji - TESTING.md §3) ───

const stack: symbol[] = [];

/** Registruje dijalog kao najgornji i vraca njegov id. */
export function pushDialog(label = 'dialog'): symbol {
  const id = Symbol(label);
  stack.push(id);
  return id;
}

/** Skida dijalog sa steka (bezbedno i ako vise nije na vrhu). */
export function popDialog(id: symbol): void {
  const i = stack.indexOf(id);
  if (i >= 0) stack.splice(i, 1);
}

/** Da li je bas ovaj dijalog trenutno najgornji - jedini koji sme na Escape. */
export function isTopDialog(id: symbol): boolean {
  return stack.length > 0 && stack[stack.length - 1] === id;
}

/** Samo za testove - broj trenutno otvorenih dijaloga. */
export function openDialogCount(): number {
  return stack.length;
}

/**
 * @param onClose  poziva se kad korisnik pritisne Escape
 * @param enabled  `false` za dijalog koji trenutno nije otvoren (inline blokovi
 *                 unutar vece komponente ne smeju uslovno da zovu hook)
 */
export function useEscapeToClose(onClose: () => void, enabled: boolean = true) {
  // Callback ide kroz ref da promena njegovog identiteta pri svakom renderu ne
  // bi stalno skidala i vracala dijalog na stek (i menjala ko je "najgornji").
  const cb = useRef(onClose);
  cb.current = onClose;

  useEffect(() => {
    if (!enabled) return;

    const id = pushDialog();
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (!isTopDialog(id)) return;
      e.stopPropagation();
      cb.current();
    };

    window.addEventListener('keydown', onKey);
    return () => {
      popDialog(id);
      window.removeEventListener('keydown', onKey);
    };
  }, [enabled]);
}
